import { AlertCircle, FileSpreadsheet, Upload } from "lucide-react";
import { useRef, useState } from "react";
import type { ImportFilePayload } from "../types";

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_XLSX_BYTES = 1024 * 1024;

type Props = {
  onFileReady: (payload: ImportFilePayload) => void;
  disabled?: boolean;
};

type Validation = { ok: true; payload: ImportFilePayload } | { ok: false; message: string };

function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.readAsText(file);
  });
}

function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(reader.result instanceof ArrayBuffer ? reader.result : new ArrayBuffer(0));
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.readAsArrayBuffer(file);
  });
}

async function validateFile(file: File): Promise<Validation> {
  // Extensión, no File.type — el MIME llega vacío en algunos sistemas.
  const lowerName = file.name.toLowerCase();
  const isCsv = lowerName.endsWith(".csv");
  const isXlsx = lowerName.endsWith(".xlsx");
  if (!isCsv && !isXlsx) {
    return {
      ok: false,
      message:
        "El archivo debe ser .csv o .xlsx. Descargá la plantilla de Excel o exportá tu hoja como CSV.",
    };
  }
  // Espejo de los caps del backend: 5MB para CSV, 1MB para .xlsx (el Excel
  // comprime — una plantilla con 500 animales pesa menos de 100KB).
  const maxBytes = isXlsx ? MAX_XLSX_BYTES : MAX_BYTES;
  if (file.size > maxBytes) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      message: `El archivo pesa ${sizeMb}MB; el límite es ${isXlsx ? "1MB" : "5MB"}. Reducilo o dividilo en partes.`,
    };
  }
  if (isXlsx) {
    // .xlsx va crudo (binario) — el backend lo abre con exceljs.
    const data = await readAsArrayBuffer(file);
    if (data.byteLength === 0) {
      return { ok: false, message: "El archivo está vacío." };
    }
    return { ok: true, payload: { fileName: file.name, content: data } };
  }
  const text = await readAsText(file);
  if (text.trim().length === 0) {
    return { ok: false, message: "El archivo está vacío." };
  }
  return { ok: true, payload: { fileName: file.name, content: text } };
}

export function ImportUploadZone({ onFileReady, disabled = false }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    const result = await validateFile(file);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    onFileReady(result.payload);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={[
          "rounded-2xl border-2 border-dashed bg-app-surface px-6 py-12 text-center transition-colors",
          isDragOver
            ? "border-app-emerald bg-app-emerald-bg"
            : error
              ? "border-app-red"
              : "border-app-border",
          disabled ? "opacity-60" : "",
        ].join(" ")}
        data-testid="import-upload-zone"
      >
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl bg-app-surface-2 text-app-ink-2">
          <Upload size={24} strokeWidth={1.5} />
        </div>
        <p className="mb-2 font-medium text-app-ink-1">
          Arrastrá tu archivo CSV o Excel, o seleccionalo desde tu computadora
        </p>
        <p className="mb-6 text-app-ink-3 text-sm">Formatos .csv y .xlsx · hasta 5MB</p>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-2xl bg-app-ink-1 px-4 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
        >
          <FileSpreadsheet size={16} strokeWidth={2} />
          Seleccionar archivo
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx"
          className="sr-only"
          data-testid="import-file-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = "";
          }}
        />
      </div>

      {error ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-app-red bg-app-red-bg/50 px-4 py-3 text-app-red text-sm"
        >
          <AlertCircle size={18} strokeWidth={2} className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      ) : null}
    </div>
  );
}
